import PropTypes from 'prop-types';
import {
  ButtonBase,
  MenuItem,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Iconify from 'src/components/iconify';
import { PHYSICAL_DIRECTIONS, PHYSICAL_DIRECTION_LABELS } from 'src/utils/physical-api';

export const DIRECTION_FLAGS = {
  [PHYSICAL_DIRECTIONS.GE_USA]: ['circle-flags:ge', 'circle-flags:us'],
  [PHYSICAL_DIRECTIONS.USA_GE]: ['circle-flags:us', 'circle-flags:ge'],
};

const PICKER_ORDER = [PHYSICAL_DIRECTIONS.GE_USA, PHYSICAL_DIRECTIONS.USA_GE];

export const DirectionFlags = ({ direction, size }) => {
  const [from, to] = DIRECTION_FLAGS[direction] ?? [];
  return (
    <Stack direction="row" alignItems="center" spacing={size > 30 ? 2 : 1}>
      <Iconify icon={from} width={size} />
      <ArrowForwardIcon sx={{ fontSize: size * 0.6, color: 'text.secondary' }} />
      <Iconify icon={to} width={size} />
    </Stack>
  );
};

DirectionFlags.propTypes = {
  direction: PropTypes.string.isRequired,
  size: PropTypes.number,
};

DirectionFlags.defaultProps = {
  size: 24,
};

export const DirectionSelect = ({ value, onChange, sx }) => (
  <TextField
    select
    label="Direction"
    variant="filled"
    value={value ?? ''}
    onChange={(event) => onChange(event.target.value)}
    sx={sx}
    SelectProps={{
      renderValue: (selected) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <DirectionFlags direction={selected} size={18} />
          <span>{PHYSICAL_DIRECTION_LABELS[selected]}</span>
        </Stack>
      ),
    }}
  >
    {PICKER_ORDER.map((direction) => (
      <MenuItem key={direction} value={direction}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <DirectionFlags direction={direction} size={18} />
          <span>{PHYSICAL_DIRECTION_LABELS[direction]}</span>
        </Stack>
      </MenuItem>
    ))}
  </TextField>
);

DirectionSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  sx: PropTypes.object,
};

DirectionSelect.defaultProps = {
  value: '',
  sx: {},
};

const DirectionPickerModal = ({ open, onClose, onSelect }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle sx={{ pr: 6 }}>
      New order
      <Typography variant="body2" color="text.secondary">
        Choose where the parcels are going
      </Typography>
      <IconButton onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ py: 1 }}>
        {PICKER_ORDER.map((direction) => (
          <ButtonBase
            key={direction}
            onClick={() => onSelect(direction)}
            sx={{
              flex: 1,
              flexDirection: 'column',
              gap: 2,
              py: 4,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              transition: 'border-color 0.15s, background-color 0.15s',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
          >
            <DirectionFlags direction={direction} size={64} />
            <Typography variant="h6">{PHYSICAL_DIRECTION_LABELS[direction]}</Typography>
          </ButtonBase>
        ))}
      </Stack>
    </DialogContent>
  </Dialog>
);

DirectionPickerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default DirectionPickerModal;
